/**
 * fl-countdown.js
 * Web Component powering the promo bar's real-time countdown
 * (sections/figma-listicle-promo.liquid).
 *
 * Contract:
 *  - Two modes, set via `data-mode`:
 *      "daily" - counts down to just before midnight in the VISITOR'S local
 *                timezone, then rolls straight over to the next day. This is a
 *                real recurring deadline (the offer genuinely resets daily), not
 *                a fake per-visitor timer that restarts on every page load.
 *      "fixed" - (default) counts down to the single instant in `data-end`.
 *  - Reads its end time from the `data-end` attribute (ISO 8601 string).
 *  - Renders as [hidden] in markup by default. Only becomes visible if
 *    data-end parses to a valid Date AND that Date is in the future.
 *  - If data-end is missing, unparseable, or already in the past, the
 *    element is left/returned to [hidden] and no interval is started.
 *    No fallback expiry is ever invented and 00:00:00 is never shown.
 *  - Ticks once per second and clears its own interval on expiry or on
 *    disconnect (e.g. section removed in the theme editor).
 *  - Each <fl-countdown> instance manages its own interval, so multiple
 *    instances on the same page (or a re-rendered section in the theme
 *    editor) never collide.
 */
(function () {
  if (window.customElements && customElements.get('fl-countdown')) return;

  class FlCountdown extends HTMLElement {
    connectedCallback() {
      this._interval = null;
      this._daily = this.getAttribute('data-mode') === 'daily';

      if (this._daily) {
        // Real deadline: end of the current day, visitor's local time.
        this._endMs = FlCountdown.endOfToday();
      } else {
        const endAttr = this.getAttribute('data-end');
        if (!endAttr) return; // no configured end time -> stay hidden, fail silently

        const endDate = new Date(endAttr);
        if (isNaN(endDate.getTime())) return; // unparseable -> stay hidden, fail silently
        if (endDate.getTime() <= Date.now()) return; // already past -> stay hidden, fail silently

        this._endMs = endDate.getTime();
      }
      this._hrEl = this.querySelector('[data-fl-unit="hr"]');
      this._minEl = this.querySelector('[data-fl-unit="min"]');
      this._secEl = this.querySelector('[data-fl-unit="sec"]');

      // Only reveal once we know we have a real, future, parseable deadline.
      this.removeAttribute('hidden');

      this._tick();
      this._interval = window.setInterval(this._tick.bind(this), 1000);
    }

    disconnectedCallback() {
      this._clear();
    }

    _tick() {
      const remainingMs = this._endMs - Date.now();

      if (remainingMs <= 0) {
        if (this._daily) {
          // Midnight passed while the page was open: roll to the next day and
          // keep running rather than disappearing.
          this._endMs = FlCountdown.endOfToday();
          return;
        }
        this._clear();
        // Expired mid-visit: hide entirely rather than freeze at 00:00:00.
        this.setAttribute('hidden', '');
        return;
      }

      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this._setUnit(this._hrEl, hours);
      this._setUnit(this._minEl, minutes);
      this._setUnit(this._secEl, seconds);
    }

    _setUnit(el, value) {
      if (!el) return;
      el.textContent = String(value).padStart(2, '0');
    }

    // 23:59:59 today, local time. Recomputed on rollover so DST shifts and
    // day boundaries are always handled by the Date object itself.
    static endOfToday() {
      const d = new Date();
      d.setHours(23, 59, 59, 999);
      return d.getTime();
    }

    _clear() {
      if (this._interval) {
        window.clearInterval(this._interval);
        this._interval = null;
      }
    }
  }

  customElements.define('fl-countdown', FlCountdown);
})();
